import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { format, isToday } from 'date-fns';
import { colors, fontStyles, spacing } from '../../styles';

interface Props {
  totalValue: number;
  snapshotDate: Date;
}

/**
 * Display a chart for Plaid portfolios with fewer than 5 snapshots.
 * Shows the latest snapshot value with a timeline and message about future data collection.
 */
export const PlaidSingleSnapshotChart: FC<Props> = ({ totalValue, snapshotDate }) => {
  const formattedValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(totalValue);

  const formattedDate = format(snapshotDate, 'MMM d');

  // Dynamic label based on whether snapshot is from today
  const snapshotLabel = useMemo(() => {
    return isToday(snapshotDate) ? "← Today's snapshot saved" : `← Snapshot from ${formattedDate}`;
  }, [snapshotDate, formattedDate]);

  return (
    <div css={containerStyles}>
      <div css={chartAreaStyles}>
        {/* Value row with bullet and label */}
        <div css={valueRowStyles}>
          <span css={valueStyles}>{formattedValue}</span>
          <span css={bulletStyles}>●</span>
          <span css={labelStyles}>{snapshotLabel}</span>
        </div>

        {/* Vertical line from bullet to timeline */}
        <div css={verticalLineStyles} />

        {/* Timeline */}
        <div css={timelineContainerStyles}>
          <div css={timelineStyles} />
          <span css={dateStyles}>{formattedDate}</span>
        </div>
      </div>

      <div css={messageStyles}>
        We&apos;ll plot your portfolio value daily.{'\n'}
        Check back tomorrow for your trend line!
      </div>
    </div>
  );
};

const containerStyles = css`
  background-color: ${colors.fog};
  border-radius: 8px;
  padding: ${spacing.deci};
  html[data-theme='dark'] & {
    background-color: transparent;
  }
`;

const chartAreaStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${spacing.deci} 0;
`;

const valueRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${spacing.centi};
`;

const valueStyles = css`
  ${fontStyles.shark};
  color: ${colors.liquorice};
  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const bulletStyles = css`
  color: ${colors.petrolBlue};
  font-size: 12px;
  html[data-theme='dark'] & {
    color: ${colors.skyBlue};
  }
`;

const labelStyles = css`
  ${fontStyles.flea};
  color: ${colors.silver};
  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const verticalLineStyles = css`
  width: 2px;
  height: 40px;
  background-color: ${colors.cloud};
  margin-left: 4px;
  html[data-theme='dark'] & {
    background-color: ${colors.sugar};
  }
`;

const timelineContainerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${spacing.centi};
`;

const timelineStyles = css`
  width: 100%;
  height: 2px;
  background-color: ${colors.cloud};
  html[data-theme='dark'] & {
    background-color: ${colors.sugar};
  }
`;

const dateStyles = css`
  ${fontStyles.flea};
  color: ${colors.silver};
  margin-top: ${spacing.centi};
  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;

const messageStyles = css`
  ${fontStyles.flea};
  color: ${colors.silver};
  text-align: center;
  margin-top: ${spacing.deci};
  white-space: pre-line;
  html[data-theme='dark'] & {
    color: ${colors.sugar};
  }
`;
